import React from "react";

import {
  Check,
  Star,
  MessageCircle,
  ArrowRight,
} from "lucide-react";

import { WHATSAPP_URL } from "@/lib/brand";

const PLANS = [
  {
    id: "starter",

    name: "Starter Website",

    tagline: "Perfect for new businesses",

    price: "₹4,999",

    period: "one-time",

    badge: null,

    highlight: false,

    features: [
      "Modern business website",
      "Mobile responsive design",
      "WhatsApp & Call integration",
      "Contact form setup",
      "Fast loading pages",
      "Basic SEO setup",
    ],

    cta: "Get Starter",
  },

  {
    id: "growth",

    name: "Business Growth",

    tagline: "Most popular for local businesses",

    price: "₹9,999",

    period: "one-time",

    badge: "Most Popular",

    highlight: true,

    features: [
      "Premium multi-section website",
      "Google Business optimization",
      "Local SEO setup",
      "High-converting design",
      "WhatsApp automation",
      "Lead-focused landing pages",
      "Priority support",
    ],

    cta: "Choose Growth",
  },

  {
    id: "premium",

    name: "Monthly Growth",

    tagline: "SEO & marketing support",

    price: "₹7,999",

    period: "/month",

    badge: null,

    highlight: false,

    features: [
      "Monthly SEO optimization",
      "Google ranking improvements",
      "Website maintenance",
      "Performance monitoring",
      "Content updates",
      "Priority WhatsApp support",
    ],

    cta: "Start Growing",
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-20 lg:py-28 bg-gradient-to-b from-white to-slate-50"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">

        {/* Heading */}
        <div className="max-w-3xl mx-auto text-center">
          
          <div className="inline-flex items-center gap-2 bg-[var(--gf-mint)] text-[var(--gf-green-deep)] border border-green-200 px-4 py-2 rounded-full text-xs font-semibold tracking-wide">
            
            <span className="h-2 w-2 rounded-full bg-[var(--gf-green)] gf-pulse-dot" />

            Free Consultation Available
          </div>

          <h2 className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08]">
            Simple pricing for growing businesses.
          </h2>

          <p className="mt-5 text-lg text-[var(--gf-text-2)] leading-relaxed">
            Whether you need a modern website, Google visibility or ongoing growth support —
            GrowFlow has a package built for your business.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="mt-16 grid lg:grid-cols-3 gap-7 items-stretch">

          {PLANS.map((p) => (
            <div
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              className={`relative rounded-3xl p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                p.highlight
                  ? "bg-[var(--gf-primary)] text-white border border-[var(--gf-blue)] shadow-2xl lg:scale-[1.03]"
                  : "bg-white border border-[var(--gf-border)] gf-shadow-soft"
              }`}
            >
              
              {/* Badge */}
              {p.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-[var(--gf-blue)] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                  
                  <Star className="h-3 w-3 fill-current" />

                  {p.badge}
                </div>
              )}

              {/* Name */}
              <h3 className="font-display text-2xl font-extrabold">
                {p.name}
              </h3>

              {/* Tagline */}
              <p
                className={`text-sm mt-2 ${
                  p.highlight
                    ? "text-white/75"
                    : "text-[var(--gf-text-2)]"
                }`}
              >
                {p.tagline}
              </p>

              {/* Price */}
              <div className="mt-8">
                
                <div className="flex items-end gap-2">
                  
                  <span className="font-display text-5xl font-extrabold tracking-tight">
                    {p.price}
                  </span>

                  <span
                    className={`pb-2 text-sm ${
                      p.highlight
                        ? "text-white/70"
                        : "text-[var(--gf-text-2)]"
                    }`}
                  >
                    {p.period}
                  </span>
                </div>

                <p
                  className={`mt-2 text-sm ${
                    p.highlight
                      ? "text-white/70"
                      : "text-[var(--gf-text-2)]"
                  }`}
                >
                  No hidden charges
                </p>
              </div>

              {/* Features */}
              <ul
                className={`mt-8 space-y-4 flex-1 ${
                  p.highlight
                    ? "text-white/95"
                    : "text-[var(--gf-secondary)]"
                }`}
              >
                {p.features.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm"
                  >
                    <span
                      className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        p.highlight
                          ? "bg-[var(--gf-green)]"
                          : "bg-[var(--gf-mint)] text-[var(--gf-green-deep)]"
                      }`}
                    >
                      <Check
                        className={`h-3 w-3 ${
                          p.highlight ? "text-white" : ""
                        }`}
                        strokeWidth={3}
                      />
                    </span>

                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`pricing-cta-${p.id}`}
                className={`mt-10 inline-flex justify-center items-center gap-2 h-13 rounded-full font-semibold text-sm transition-all hover:-translate-y-0.5 ${
                  p.highlight
                    ? "bg-[var(--gf-green)] hover:bg-[var(--gf-green-deep)] text-white"
                    : "bg-[var(--gf-primary)] hover:bg-slate-800 text-white"
                }`}
              >
                <MessageCircle className="h-4 w-4" />

                {p.cta}

                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <p className="mt-10 text-center text-sm text-[var(--gf-text-2)]">
          50% advance to start · Fast delivery · WhatsApp support included
        </p>
      </div>
    </section>
  );
}
